import _ from 'lodash';
import { createSelector } from 'reselect';
import timm from 'timm';

import CadminAx          from 'app/actions/company-admin/companies';
import ToastAx           from 'app/actions/toast';
import CadminApi         from 'app/apis/company-admin';
import reducerUtils      from 'app/reducers/utils';
import CadminSlx         from 'app/selectors/company-admin/';
import EntitiesSlx       from 'app/selectors/entities';
import RoutingSlx        from 'app/selectors/routing';


/*
 *  Actions
 */

const Types = {
  LOAD_PAGE_CAL: 'CA_INTS_LOAD_PAGE_CAL',
  LOAD_CALENDARS: 'CA_INTS_LOAD_CALENDARS',
  UPDATE_CALENDARS: 'CA_INTS_UPDATE_CALENDARS',
  GRANTS_DESTROY: 'CA_INTS_GRANTS_DESTROY',
};

const Ax = {

  loadPageCalendar: ({params}) => (dispatch, getState) => {
    const promise = Promise.all([
      dispatch(CadminAx.loadCommon(params.companySlug)),
      dispatch(Ax.loadCalendars(params.companySlug)),
    ]);
    return dispatch({type: Types.LOAD_PAGE_CAL, promise});
  },

  loadCalendars: (companySlug, {forceSync=false, forceFetch=false}={}) => (dispatch, getState) => {
    if (!companySlug) companySlug = CadminSlx.company(getState())?.slug;
    const hasLoaded = Slx.nylasCompanySlug(getState()) === companySlug;
    const canSkip = hasLoaded && !forceSync && !forceFetch;
    if (canSkip) return Promise.resolve();

    const promise = CadminApi.intsNylasLoadAll(companySlug, {forceSync});
    return dispatch({type: Types.LOAD_CALENDARS, promise, companySlug, _entities: ['company']});
  },

  updateCalendars: (companyId, nylasGrantId, params) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.intsNylasUpdateCalendars(companyId, nylasGrantId, params);
    promise.then(() => {
      dispatch(Ax.loadCalendars(undefined, {forceFetch: true}));
    });
    return dispatch({type: Types.UPDATE_CALENDARS, promise});
  },

  grantsDestroy: (companyId, nylasGrantId) => (dispatch, getState) => {
    if (!companyId) companyId = CadminSlx.companyId(getState());
    const promise = CadminApi.intsNylasGrantsDestroy(companyId, nylasGrantId);
    promise.then(() => {
      dispatch(Ax.loadCalendars(undefined, {forceFetch: true}));
    });
    return dispatch({type: Types.GRANTS_DESTROY, promise, nylasGrantId});
  },

};



/*
 *  Reducer
 */

const initialState = {
  nylasCompanySlug: null,
  nylasGrants: null,
  nylasCalendars: null,
  nylasUpdatePending: false,
  nylasGrantsDestroyPendingId: null,
};

const reducer = reducerUtils.createReducer(initialState, {

  [`${Types.LOAD_CALENDARS}_PENDING`]: (state, action) => {
    if (state.nylasCompanySlug === action.companySlug) return state;
    return {...state,
      nylasCompanySlug: action.companySlug,
      nylasGrants: null,
    };
  },
  [`${Types.LOAD_CALENDARS}_RESOLVED`]: (state, action) => {
    if (state.nylasCompanySlug !== action.companySlug) return state;
    return {...state,
      nylasGrants: action.result.nylasGrants,
      nylasCalendars: action.result.nylasCalendars,
    };
  },
  // [`${Types.LOAD_CALENDARS}_REJECTED`]: (state, action) => {},

  [`${Types.UPDATE_CALENDARS}_PENDING`]: (state, action) => {
    return {...state, nylasUpdatePending: true};
  },
  [`${Types.UPDATE_CALENDARS}_RESOLVED`]: (state, action) => {
    return {...state, nylasUpdatePending: false};
  },
  [`${Types.UPDATE_CALENDARS}_REJECTED`]: (state, action) => {
    return {...state, nylasUpdatePending: false};
  },

  [`${Types.GRANTS_DESTROY}_PENDING`]: (state, action) => {
    return {...state, nylasGrantsDestroyPendingId: action.nylasGrantId};
  },
  [`${Types.GRANTS_DESTROY}_RESOLVED`]: (state, action) => {
    return {...state, nylasGrantsDestroyPendingId: false};
  },
  [`${Types.GRANTS_DESTROY}_REJECTED`]: (state, action) => {
    return {...state, nylasGrantsDestroyPendingId: false};
  },

});



/*
 *  Selectors
 */

const Slx = (() => {

  const selNylasCompanySlug            = (state) => state.companyAdmin.integrations.nylasCompanySlug;
  const selNylasGrants                 = (state) => state.companyAdmin.integrations.nylasGrants;
  const selNylasCalendars              = (state) => state.companyAdmin.integrations.nylasCalendars;
  const selNylasUpdatePending          = (state) => state.companyAdmin.integrations.nylasUpdatePending;
  const selNylasGrantsDestroyPendingId = (state) => state.companyAdmin.integrations.nylasGrantsDestroyPendingId;

  const selAllCalendars = createSelector(
    [selNylasCalendars, selNylasGrants],
    (cals, grants) => {
      if (!cals || !grants) return null;
      return cals.map((cal) => {
        const grant = grants.find(g => cal.nylasGrantId === g.id)
        return {...cal, nylasGrant: grant};
      });
    }
  );

  const selValidCalendars = createSelector(
    [selAllCalendars],
    (allCals) => {
      if (!allCals) return null;
      return allCals.filter((cal) => {
        if (cal.readOnly) return false;
        if (cal.disabled) return false;
        if (cal.deleted) return false;
        if (!cal.nylasGrant) return false;
        return true;
      });
    }
  );

  const selDefaultCalendar = createSelector(
    [selValidCalendars, CadminSlx.company],
    (cals, company) => {
      if (!company?.defaultNylasCalendarId) return null;
      return (cals || []).find(cal => cal.id === company.defaultNylasCalendarId) || null;
    }
  );

  return {
    nylasCompanySlug: selNylasCompanySlug,
    nylasGrants: selNylasGrants,
    nylasCalendars: selNylasCalendars,
    nylasUpdatePending: selNylasUpdatePending,
    allCalendars: selAllCalendars,
    validCalendars: selValidCalendars,
    nylasGrantsDestroyPendingId: selNylasGrantsDestroyPendingId,
    defaultCalendar: selDefaultCalendar,
  };

})();



export {Types, Ax, reducer, Slx};
export default {Types, Ax, reducer, Slx};
